import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  CmdModal,
  CmdModalFooterButton,
  CmdSimpleSelect,
} from '@commander-services/gui-components';
import dayjs from 'dayjs';
import { useRecoilValue } from 'recoil';
import { Controller, useForm } from 'react-hook-form';
import { useQueries } from '@tanstack/react-query';
import FormikValidatorService, {
  IValidationApiResponse,
} from '../../../Services/FormikValidatorService';
import { IOption } from '../../Forms/CmdField';
import Label from '../../Forms/Fields/Label';
import usePageWithForm from '../../../hooks/usePageWithForm';
import {
  ICheckedTableItems,
  IFilterTripsOverview,
  ITripsOverViewRecord,
  ITripsOverviewAdvancedFilterForRequest,
} from '../interfaces';
import { transformErrors } from '../../../Services/HookFormService';
import { selectedVehiclesAtom } from '../../../store/recoil/vehicles';
import {
  advancedFiltersAtom,
  checkedTableItemsAtom,
  filterForRequestAtom,
  isActiveAdvanceFilterAtom,
} from '../TripsOverviewState';
import {
  changeDriver,
  convertTimeToSeconds,
  getDrivers,
  handleFilterKey,
} from '../TripsOverviewService';
import useAnalytics from '../../../hooks/useAnalytics';
import { GDPR_VALUE } from '../../CmdTable/constants';

interface IChangeDriverProps {
  closeCallback: () => void;
  data: ITripsOverViewRecord | null;
  type: string;
  updateTable: () => void;
}

export default function ChangeDriver(props: IChangeDriverProps): JSX.Element {
  usePageWithForm();
  const { formatMessage: f } = useIntl();
  const selectedVehicles = useRecoilValue<number[]>(selectedVehiclesAtom);
  const filterForRequest = useRecoilValue<IFilterTripsOverview>(filterForRequestAtom);
  const [isLongStart, setIsLongStart] = React.useState<boolean>(false);
  const [isLongEnd, setIsLongEnd] = React.useState<boolean>(false);
  const [isLongVehicle, setIsLongVehicle] = React.useState<boolean>(false);
  const checkedTableItems = useRecoilValue<ICheckedTableItems>(checkedTableItemsAtom);
  const [isLoaderActive, setIsLoaderActive] = React.useState<boolean>(false);
  const [options, setOptions] = React.useState<IOption[]>([]);
  const isActiveAdvanceFilter = useRecoilValue(isActiveAdvanceFilterAtom);
  const advancedFilterValues = useRecoilValue(advancedFiltersAtom);
  const { trackEvent } = useAnalytics();

  const validateFields = (values) => {
    let formErrors = {};
    const validation: IValidationApiResponse = {
      driverId: [{ param: 'required', value: true, message: f({ id: 'form.required' }) }],
    };
    formErrors = FormikValidatorService.validateByValidationApiResponse(values, validation);
    return transformErrors(formErrors);
  };

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<{ driverId: string }>({
    mode: 'onChange',
    defaultValues: {
      driverId: '',
    },
    resolver: async (data) => {
      const formErrors = validateFields(data);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const getDriversData = async () => {
    const customerId = props.data?.customer?.id || checkedTableItems.customerId;
    if (customerId) {
      const driverOptions = await getDrivers(String(customerId));
      setOptions(driverOptions || []);
    }
  };

  useQueries({
    queries: [
      {
        queryKey: ['tripsOverviewDrivers'],
        queryFn: () => getDriversData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const onSubmit = async (values) => {
    trackEvent(['ride book', 'click', 'Ride book change driver']);
    setIsLoaderActive(true);
    const advancedFilters: ITripsOverviewAdvancedFilterForRequest = handleFilterKey(
      {
        ...advancedFilterValues,
        timeFrom: convertTimeToSeconds(advancedFilterValues.timeFrom),
        timeTo: convertTimeToSeconds(advancedFilterValues.timeTo),
      },
      isActiveAdvanceFilter
    );
    const filter = {
      vehicle: selectedVehicles,
      ...advancedFilters,
      ...filterForRequest,
    };

    const res = await changeDriver(values, props.data, checkedTableItems, filter);
    setIsLoaderActive(false);
    if (res) {
      props.closeCallback();
      props.updateTable();
    }
  };

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'close-modal',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'form.cancel' }),
      tooltip: f({ id: 'rides.modal.driver.cancel.tooltip' }),
      closeCallback: props.closeCallback,
    },
    {
      id: 'changedriver-form-submit',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'tripsOverview.modal.toChangeDriver' }),
      tooltip: f({ id: 'rides.modal.driver.submit.tooltip' }),
      submitCallback: handleSubmit(onSubmit),
    },
  ];

  React.useEffect(() => {
    if (props.data) {
      const startId = document.getElementById(`ride-start-${props.data.timestamp.start}`);
      if (startId && !isLongStart && startId.clientHeight > 19) {
        setIsLongStart(true);
      }
      const endId = document.getElementById(`ride-end-${props.data.timestamp.end}`);
      if (endId && !isLongEnd && endId.clientHeight > 19) {
        setIsLongEnd(true);
      }
      const vehicleId = document.getElementById(`vehicle-${props.data.vehicle.id}`);
      if (vehicleId && !isLongVehicle && vehicleId.clientHeight > 19) {
        setIsLongVehicle(true);
      }
    }
  }, [isLongEnd, isLongStart, isLongVehicle, props.data]);

  const getEndValue = () => {
    const address = props.data?.address.startLink
      ? props.data.address.end
      : f({ id: 'tables.tripsOverview.column.datetime.anonymized' });
    if (props.data?.timestamp.end === GDPR_VALUE) {
      return (
        <>
          <i className="cmd-icons cmd-icon-gdpr-document-lock-dark-gray" /> - {address}
        </>
      );
    }
    if (props.data?.timestamp.end) {
      return `${dayjs(props.data.timestamp.end).format('DD.MM.YYYY HH:mm:ss')} - ${address}`;
    }
    return '';
  };

  return (
    <CmdModal
      id="driver-form-modal"
      title={`${f({ id: 'rides.driverChanging' })}`}
      footerButtons={buttons}
      loader={isLoaderActive}
    >
      {props.data && (
        <div style={{ marginBottom: '24px' }}>
          <Label
            title={f({ id: 'tripsOverview.modal.vehicle' })}
            value={`${props.data.vehicle.licenceNumber} - ${props.data.vehicle.name}`}
            isLong={isLongVehicle}
            name={`vehicle-${props.data.vehicle.id}`}
            newStyle
          />
          {props.data && (
            <Label
              title={f({ id: 'tripsOverview.modal.rideStart' })}
              value={
                props.data.timestamp.start &&
                `${dayjs(props.data.timestamp.start).format('DD.MM.YYYY HH:mm:ss')}
                        - ${
                          props.data.address.startLink
                            ? props.data.address.start
                            : f({ id: 'tables.tripsOverview.column.datetime.anonymized' })
                        }`
              }
              newStyle
              isLong={isLongStart}
              name={`ride-start-${props.data.timestamp.start}`}
            />
          )}
          {props.data.timestamp.end && (
            <Label
              title={f({ id: 'tripsOverview.modal.rideEnd' })}
              value={getEndValue()}
              newStyle
              isLong={isLongEnd}
              name={`ride-end-${props.data.timestamp.end}`}
            />
          )}
        </div>
      )}
      <Controller
        name="driverId"
        control={control}
        rules={{
          required: errors.driverId ? errors.driverId.message : undefined,
        }}
        render={({ field }) => (
          <CmdSimpleSelect
            {...field}
            id="driverId"
            isWithSearchInput
            introOption={f({ id: 'form.choose' })}
            searchPlaceholder={f({ id: 'form.search' })}
            label={f({ id: 'tripsOverview.modal.driver' })}
            required={true}
            options={options}
            onChangeCallback={(value: string) => {
              setValue('driverId', value);
              trigger('driverId');
            }}
            error={errors.driverId?.message}
            touched={true}
          />
        )}
      />
    </CmdModal>
  );
}
